/**
 * Ce fichier permet de visualiser le tracé de l'avion dans la carte 3D
 * © Moustapha HENNAWI - Click and Take off - 2021
 */
/*-------------------------------------------------------------------------------------------*/
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
import PropTypes from 'prop-types';

import { useEffect, useState } from 'react';
import { useEffectAsync } from '../reactHelper';
import { map, tb } from './Map';

import PlaneModels from '../resources/images/models/plane.glb';
import { mapFitBound } from './mapUtil';
// ----------------------------------------------------------------------

const MAP_HANDLER = ['boxZoom', 'scrollZoom', 'dragPan'];

function animate() {
  requestAnimationFrame(animate);
}

const api = {
  fixedZoom: true,
  pan: true,
  maxZoom: 16
};

function init() {
  animate();
}

// ----------------------------------------------------------------------

ReplayPathMap3d.propTypes = {
  positions: PropTypes.array,
  events: PropTypes.array,
  analyse: PropTypes.object,
  state: PropTypes.object,
  setState: PropTypes.func,
  setIndexDisplay: PropTypes.func,
  isDown: PropTypes.bool
};

export default function ReplayPathMap3d({
  positions,
  events,
  analyse,
  state,
  setState,
  setIndexDisplay,
  isDown
}) {
  const [plane, setPlaneModel] = useState();
  const [allLine, setAllLine] = useState([]);

  const coordinates = (() => {
    const coordinates = [];
    for (let i = 0; i < positions.length; i += 1) {
      coordinates.push([positions[i].longitude, positions[i].latitude, positions[i].altitude]);
    }
    return coordinates;
  })();

  const mapConfig = {
    MAD: {
      origin: [
        positions[0].longitude,
        positions[0].latitude,
        positions[0].attributes && positions[0].altitude - positions[0].attributes.elevation <= 5
          ? positions[0].attributes.elevation + 5
          : positions[0].altitude
      ],
      originCourse: positions[0].course,
      destination: [
        positions[positions.length - 1].longitude,
        positions[positions.length - 1].latitude,
        positions[positions.length - 1].attributes &&
        positions[positions.length - 1].altitude -
          positions[positions.length - 1].attributes.elevation <=
          0
          ? positions[positions.length - 1].attributes.elevation + 5
          : positions[positions.length - 1].altitude
      ],
      center: [
        positions[0].longitude,
        positions[0].latitude,
        positions[0].attributes && positions[0].altitude - positions[0].attributes.elevation <= 5
          ? positions[0].attributes.elevation + 5
          : positions[0].altitude
      ],
      current: [
        positions[state.currentPosition].longitude,
        positions[state.currentPosition].latitude,
        positions[state.currentPosition].attributes &&
        positions[state.currentPosition].altitude -
          positions[state.currentPosition].attributes.elevation <=
          0
          ? positions[state.currentPosition].attributes.elevation + 5
          : positions[state.currentPosition].altitude
      ],
      zoom: 20,
      pitch: 50,
      bearing: -13,
      scale: 0.1,
      timezone: 'Europe/Paris'
    },
    names: {
      customLayer: 'custom-layer'
    }
  };

  const onObjectChanged = async (e) => {
    if (state.playVol) {
      const model = e.detail.object; // here's the object already modified
      const positionIndex = positions.indexOf(
        positions.find(
          (item) =>
            item.longitude > model.coordinates[0] - 0.002 &&
            item.longitude < model.coordinates[0] + 0.002 &&
            item.latitude > model.coordinates[1] - 0.001 &&
            item.latitude < model.coordinates[1] + 0.001
        )
      );

      if (positionIndex !== -1) {
        const position = [model.coordinates[0], model.coordinates[1]];
        const altitude =
          positions[positionIndex].attributes &&
          positions[positionIndex].altitude - positions[positionIndex].attributes.elevation <= 5
            ? positions[positionIndex].attributes.elevation + 10
            : model.coordinates[2] + 100;

        const camera = map.getFreeCameraOptions();
        camera.position = mapboxgl.MercatorCoordinate.fromLngLat(position, altitude + 5);
        camera.lookAtPoint([model.coordinates[0], model.coordinates[1]]);
        camera.setPitchBearing(80, positions[positionIndex].course);
        map.setFreeCameraOptions(camera);

        map.setPitch(80);
        map.setBearing(positions[positionIndex].course);
        if (altitude > 350) {
          map.setZoom(map.getZoom() - 0.25);
        } else if (altitude > 260 && altitude < 350) {
          map.setZoom(map.getZoom() - 0.4);
        } else {
          map.setZoom(map.getZoom() - 0.6);
        }

        setIndexDisplay(positionIndex);
      }
    }

    tb.map.repaint = true;
  };

  function fly() {
    if (
      coordinates !== null &&
      coordinates !== undefined &&
      plane !== null &&
      plane !== undefined
    ) {
      // extract path geometry from callback geojson, and set duration of travel
      const options = {
        path: coordinates,
        duration: 800000 / state.speedReplay,
        loop: true
      };
      // start the truck animation with above options, and remove the line when animation ends
      plane.followPath(options, () => {
        plane.stop();
        plane.setCoords(mapConfig.MAD.origin);
        plane.setRotation({ x: 0, y: 0, z: 180 - mapConfig.MAD.originCourse });
        map.flyTo({
          center: mapConfig.MAD.origin,
          pitch: 0,
          bearing: 0,
          zoom: 20
        });
        setState({ ...state, playVol: false });
      });
    }
  }

  function createLine(coordinate, name, color) {
    if (coordinate !== null && coordinate !== undefined) {
      const line = tb.line({
        geometry: coordinate,
        width: isDown ? 5 : 8,
        color
      });

      setAllLine((allLine) => [...allLine, line]);
      tb.add(line, name);
    }
  }

  function createLinePhase(phase, phaseName, color, index) {
    const start =
      phaseName === 'decollage'
        ? positions.indexOf(
            positions.find(
              (position) =>
                position.id ===
                (phase.startPositionId === 0 ? phase.pointPositionId : phase.startPositionId)
            )
          )
        : positions.indexOf(positions.find((position) => position.id === phase.startPositionId));
    const end =
      phaseName === 'atterrissage'
        ? positions.indexOf(
            positions.find(
              (position) =>
                position.id ===
                (phase.endPositionId === 0 ? phase.pointPositionId : phase.endPositionId)
            )
          )
        : positions.indexOf(positions.find((position) => position.id === phase.endPositionId));

    const startGeofences = events
      .filter((event) => event.type === 'geofenceEnter')
      .map((event) => positions.indexOf(positions.find((item) => item.id === event.positionId)))
      .filter((geofence) => geofence >= start && geofence <= end);

    const exitGeofences = events
      .filter((event) => event.type === 'geofenceExit')
      .map((event) => positions.indexOf(positions.find((item) => item.id === event.positionId)))
      .filter((geofence) => geofence >= start && geofence <= end);

    if (startGeofences.length !== 0) {
      // eslint-disable-next-line array-callback-return
      startGeofences.map((startGeofence, indexGeofence) => {
        const exitGeofence = exitGeofences[indexGeofence];

        if (indexGeofence === 0) {
          const coord = positions
            .filter((position, i) => i >= start && i <= startGeofence)
            .map((position) => [
              position.longitude,
              position.latitude,
              position.attributes && position.altitude - position.attributes.elevation <= 5
                ? position.attributes.elevation + 5
                : position.altitude
            ]);

          if (coord.length !== 0) {
            createLine(coord, `${phaseName}Enter${index}`, color);
          }
        }
        if (indexGeofence === exitGeofences.length - 1) {
          const coord = positions
            .filter((item, i) => i >= exitGeofence && i <= end)
            .map((position) => [
              position.longitude,
              position.latitude,
              position.attributes && position.altitude - position.attributes.elevation <= 5
                ? position.attributes.elevation + 5
                : position.altitude
            ]);
          if (coord.length !== 0) {
            createLine(coord, `${phaseName}Exit${index}`, color);
          }
        }

        if (
          indexGeofence < startGeofences.length - 1 &&
          indexGeofence + 1 <= startGeofences.length - 1
        ) {
          const coord = positions
            .filter((item, i) => i >= exitGeofence && i <= startGeofences[indexGeofence + 1])
            .map((position) => [
              position.longitude,
              position.latitude,
              position.attributes && position.altitude - position.attributes.elevation <= 5
                ? position.attributes.elevation + 5
                : position.altitude
            ]);

          if (coord.length !== 0) {
            createLine(coord, `${phaseName}InterLine${index}`, color);
          }
        }

        const coord =
          exitGeofence === undefined
            ? positions
                .filter((item, i) => i >= startGeofence && i <= end)
                .map((position) => [
                  position.longitude,
                  position.latitude,
                  position.attributes && position.altitude - position.attributes.elevation <= 5
                    ? position.attributes.elevation + 5
                    : position.altitude
                ])
            : positions
                .filter((item, i) => i >= startGeofence && i <= exitGeofence)
                .map((position) => [
                  position.longitude,
                  position.latitude,
                  position.attributes && position.altitude - position.attributes.elevation <= 5
                    ? position.attributes.elevation + 5
                    : position.altitude
                ]);

        if (coord.length !== 0) {
          createLine(coord, `${phaseName}Geofence${index}`, '#bb0b0b');
        }
      });
    } else {
      const coord = positions
        .filter((position, i) => i >= start && i <= end)
        .map((position) => [
          position.longitude,
          position.latitude,
          position.attributes && position.altitude - position.attributes.elevation <= 5
            ? position.attributes.elevation + 5
            : position.altitude
        ]);

      if (coord.length !== 0) {
        createLine(coord, phaseName + index, color);
      }
    }

    return null;
  }

  const createAllLine = () => {
    switch (
      state.currentPhase !== 'all' && state.selectedListItem === -1 ? 'all' : state.currentPhase
    ) {
      case 'roulages': {
        const item = analyse.analyseRoulageReports[state.selectedListItem];
        if (item) {
          createLinePhase(item, 'roulage', '#000000', state.selectedListItem);
        }
        break;
      }

      case 'decollages': {
        const item = analyse.analyseDecollageReports[state.selectedListItem];
        if (item) {
          createLinePhase(item, 'decollage', '#22398d', state.selectedListItem);
        }

        break;
      }
      case 'vols': {
        const item = analyse.analyseVolReports[state.selectedListItem];
        if (item) {
          createLinePhase(item, 'vol', '#095228', state.selectedListItem);
        }
        break;
      }
      case 'atterrissages': {
        const item = analyse.analyseAtterrissageReports[state.selectedListItem];
        if (item) {
          createLinePhase(item, 'atterrissage', '#e50e58', state.selectedListItem);
        }
        break;
      }
      default:
        if (
          analyse.analyseAtterrissageReports.length !== analyse.analyseDecollageReports.length ||
          (analyse.analyseAtterrissageReports.length === 0 &&
            analyse.analyseDecollageReports.length === 0)
        ) {
          createLine(coordinates, 'Line', '#800080');
        }
        analyse.analyseAtterrissageReports.map((item, index) =>
          createLinePhase(item, 'atterrissage', '#e50e58', index)
        );

        analyse.analyseDecollageReports.map((item, index) =>
          createLinePhase(item, 'decollage', '#22398d', index)
        );

        analyse.analyseRoulageReports.map((item, index) =>
          createLinePhase(item, 'roulage', '#000000', index)
        );

        analyse.analyseVolReports.map((item, index) =>
          createLinePhase(item, 'vol', '#095228', index)
        );
        break;
    }
  };

  /** ***************************************************************************************************************************************************************** */
  useEffectAsync(async () => {
    await init();
    map.addLayer(
      {
        id: 'custom_layer',
        type: 'custom',
        renderingMode: '3d',
        onAdd: () => {
          // Creative Commons License attribution: Plane model by https://sketchfab.com/ideehochzwei
          // from  https://sketchfab.com/3d-models/plane-aa001f5a88f64b16b98356c042f2d5f3
          let planeload;

          tb.altitudeStep = 1;

          const options = {
            obj: PlaneModels,
            type: 'gltf',
            scale: mapConfig.MAD.scale,
            rotation: { x: 90, y: 0, z: 0 },
            anchor: 'auto',
            adjustment: {
              x: 0,
              y: 0,
              z: 0
            },
            bbox: false,
            clone: false
          };

          if (api.fixedZoom) options.fixedZoom = api.maxZoom;

          tb.loadObj(options, async (model) => {
            planeload = model.setCoords(mapConfig.MAD.origin);
            planeload.setRotation({
              x: 0,
              y: 0,
              z: 180 - mapConfig.MAD.originCourse
            });
            planeload.addEventListener('ObjectChanged', onObjectChanged, false);
            planeload.castShadow = false;

            setPlaneModel(planeload);

            tb.add(planeload);
            tb.map.repaint = true;
            /** *************************************************************************** Create line */
          });
        },
        render: () => {
          tb.update();
        }
      },
      'geofences-fill-P',
      'geofences-fill-A',
      'geofences-fill-Vac'
    );

    return () => {
      if (plane !== undefined) {
        plane.stop();
      }
      // tb.removeEventListener('ObjectChanged');
      setPlaneModel(null);
      tb.clear();
      if (map.getLayer('custom_layer')) {
        map.removeLayer('custom_layer');
      }
    };
  }, []);

  useEffect(() => {
    if (plane !== undefined && plane !== null) {
      plane.setCoords([
        positions[state.currentPosition].longitude,
        positions[state.currentPosition].latitude,
        positions[state.currentPosition].attributes &&
        positions[state.currentPosition].altitude -
          positions[state.currentPosition].attributes.elevation <=
          0
          ? positions[state.currentPosition].attributes.elevation + 5
          : positions[state.currentPosition].altitude
      ]);
      plane.setRotation({
        x: 0,
        y: 0,
        z: 180 - positions[state.currentPosition].course
      });
    }
  }, [plane, positions, state.currentPosition]);

  useEffectAsync(async () => {
    await allLine.map((item) => tb.remove(item));
    await setAllLine([]);
    createAllLine();

    if (state.currentPhase !== 'all' && state.selectedListItem !== -1) {
      let phase = null;

      if (state.currentPhase === 'atterrissages') {
        phase = analyse.analyseAtterrissageReports[state.selectedListItem];
      } else if (state.currentPhase === 'decollages') {
        phase = analyse.analyseDecollageReports[state.selectedListItem];
      } else if (state.currentPhase === 'roulages') {
        phase = analyse.analyseRoulageReports[state.selectedListItem];
      } else if (state.currentPhase === 'vols') {
        phase = analyse.analyseVolReports[state.selectedListItem];
      }
      if (phase !== undefined && phase !== null) {
        const startId =
          state.currentPhase === 'decollages' && phase.startPositionId === 0
            ? phase.pointPositionId
            : phase.startPositionId;

        const endId =
          state.currentPhase === 'atterrissages' && phase.endPositionId === 0
            ? phase.pointPositionId
            : phase.endPositionId;

        const start = positions.indexOf(positions.find((item) => item.id === startId));
        const end = positions.indexOf(positions.find((item) => item.id === endId));

        const newCoord = positions
          .filter((position, index) => index >= start && index <= end)
          .map((position) => [position.longitude, position.latitude, position.altitude]);

        mapFitBound(newCoord);
      }
    } else {
      mapFitBound(coordinates);
    }

    return () => {
      setAllLine([]);
    };
  }, [
    state.selectedListItem,
    analyse.analyseAtterrissageReports,
    analyse.analyseDecollageReports,
    analyse.analyseRoulageReports,
    analyse.analyseVolReports,
    isDown
  ]);

  useEffect(() => {
    map.panTo(
      [positions[state.currentPosition].longitude, positions[state.currentPosition].latitude],
      {
        duration: 500
      }
    );
  }, [positions, state.currentPosition]);

  useEffectAsync(async () => {
    if (plane !== undefined && plane !== null) {
      plane.addEventListener('ObjectChanged', onObjectChanged, false);

      if (state.playVol) {
        MAP_HANDLER.forEach((h) => (h = map[h]) && h.enable && h.disable && h.disable());
        await createAllLine();
        const planeAltitude = plane.coordinates[2];
        let dynamicZoom;
        if (planeAltitude <= 150) dynamicZoom = 18;
        else if (planeAltitude >= 300) dynamicZoom = 14;
        else dynamicZoom = -(1 / 37.5) * planeAltitude + 22;

        map.easeTo({
          center: mapConfig.MAD.origin,
          zoom: dynamicZoom,
          easing(t) {
            if (t === 1) {
              fly();
            }
            return t;
          }
        });
      } else {
        MAP_HANDLER.forEach((h) => (h = map[h]) && h.enable && h.disable && h.enable());
        plane.setCoords(mapConfig.MAD.origin);
        plane.setRotation({ x: 0, y: 0, z: 180 - mapConfig.MAD.originCourse });
      }
    }
  }, [state.playVol]);

  return null;
}
